"use client";

import {
  type CloneForCaptureResult,
  copyElementAsPng,
  downloadElementAsPng,
} from "@/lib/capture";
import { sanitizeFilename } from "@/lib/capture/filename";

type VizRow = Record<string, unknown>;
const TRANSPARENT_BACKGROUND_VALUES = new Set([
  "",
  "transparent",
  "rgba(0, 0, 0, 0)",
  "rgba(0,0,0,0)",
]);
const VIZ_CARD_ROOT_SELECTOR = '[data-viz-card-root="true"]';
const VIZ_CAPTURE_HORIZONTAL_SCROLLPORT_SELECTOR =
  '[data-viz-capture-horizontal-scrollport="true"]';
const VIZ_CAPTURE_HORIZONTAL_CONTENT_SELECTOR =
  '[data-viz-capture-horizontal-content="true"]';

type HorizontalScrollportMeasurement = {
  index: number;
  scrollWidth: number;
  visibleWidth: number;
};

function removeTimestampPunctuation(value: string): string {
  let result = "";
  for (const char of value) {
    if (
      char === "-" ||
      char === ":" ||
      char === "T" ||
      char === "Z" ||
      char === "."
    ) {
      continue;
    }
    result += char;
  }
  return result;
}

function escapeDoubleQuotes(value: string): string {
  return value.split('"').join('""');
}

function timestampSuffix(): string {
  return removeTimestampPunctuation(new Date().toISOString()).slice(0, 14);
}

function buildExportFileBaseName(baseName: string): string {
  const sanitized = sanitizeFilename(baseName);
  const prefix = sanitized.length > 0 ? sanitized : "viz";
  return `${prefix}_${timestampSuffix()}`;
}

function escapeCsvValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  const raw = String(value);
  const escaped = escapeDoubleQuotes(raw);
  return `"${escaped}"`;
}

function downloadBlob(blob: Blob, filename: string): void {
  if (typeof document === "undefined") return;
  const url = URL.createObjectURL(blob);

  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * PNG 化前に広げる必要がある横スクロール領域だけを計測する。
 *
 * @remarks
 * チャット上では狭い画面で横スクロールを許可するが、書き出し時は可視範囲ではなく
 * グラフ全幅を保存したい。DOM の `scrollWidth` と表示幅の差分を保持して、clone 側だけを
 * 安全に拡張できるようにする。
 */
function measureVizHorizontalScrollports(
  root: HTMLElement,
): HorizontalScrollportMeasurement[] {
  const scrollports = Array.from(
    root.querySelectorAll<HTMLElement>(
      VIZ_CAPTURE_HORIZONTAL_SCROLLPORT_SELECTOR,
    ),
  );

  return scrollports
    .map((scrollport, index) => {
      const content = scrollport.querySelector<HTMLElement>(
        VIZ_CAPTURE_HORIZONTAL_CONTENT_SELECTOR,
      );
      const scrollWidth = Math.ceil(
        Math.max(scrollport.scrollWidth, content?.scrollWidth ?? 0),
      );
      const visibleWidth = Math.ceil(scrollport.clientWidth);

      return {
        index,
        scrollWidth,
        visibleWidth,
      };
    })
    .filter((measurement) => {
      return measurement.scrollWidth > measurement.visibleWidth;
    });
}

/**
 * 横スクロールで隠れている AI 可視化グラフを、全幅キャプチャ用の clone に差し替える。
 *
 * @remarks
 * 画面表示ではカード幅を保ったまま chart 部分だけ横スクロールさせる。一方で PNG 書き出し
 * は共有物なので、スクロール位置に依存して一部だけ切り出されないよう、オフスクリーンの
 * 複製カードだけを不足幅ぶん広げてから capture に渡す。
 */
function cloneVizCardWithExpandedHorizontalScrollports(
  original: HTMLElement,
): CloneForCaptureResult | null {
  if (typeof document === "undefined") {
    return null;
  }

  const measurements = measureVizHorizontalScrollports(original);
  if (measurements.length === 0) {
    return null;
  }

  const maximumHiddenWidth = Math.max(
    ...measurements.map(
      (measurement) => measurement.scrollWidth - measurement.visibleWidth,
    ),
  );
  const captureWidth = Math.max(
    original.offsetWidth,
    original.scrollWidth,
    original.offsetWidth + maximumHiddenWidth,
  );

  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.top = "0";
  container.style.left = "0";
  container.style.pointerEvents = "none";
  container.style.opacity = "0";
  container.style.zIndex = "-1";
  container.style.isolation = "isolate";
  container.style.display = "block";
  container.style.width = `${captureWidth}px`;

  const clone = original.cloneNode(true) as HTMLElement;
  clone.style.width = `${captureWidth}px`;
  clone.style.maxWidth = "none";
  clone.style.overflow = "visible";

  const cloneScrollports = Array.from(
    clone.querySelectorAll<HTMLElement>(
      VIZ_CAPTURE_HORIZONTAL_SCROLLPORT_SELECTOR,
    ),
  );

  measurements.forEach((measurement) => {
    const scrollport = cloneScrollports[measurement.index];
    if (!scrollport) {
      return;
    }

    scrollport.style.width = `${measurement.scrollWidth}px`;
    scrollport.style.maxWidth = "none";
    scrollport.style.overflowX = "visible";
    scrollport.style.overflowY = "visible";
    scrollport.style.paddingBottom = "0";

    const content = scrollport.querySelector<HTMLElement>(
      VIZ_CAPTURE_HORIZONTAL_CONTENT_SELECTOR,
    );
    if (content) {
      content.style.width = `${measurement.scrollWidth}px`;
      content.style.maxWidth = "none";
      content.style.overflow = "hidden";
    }
  });

  container.appendChild(clone);
  document.body.appendChild(container);

  return {
    element: clone,
    cleanup: () => {
      container.remove();
    },
  };
}

function normalizeBackgroundColor(value: string): string {
  return value.trim().toLowerCase();
}

function isTransparentBackgroundColor(value: string): boolean {
  return TRANSPARENT_BACKGROUND_VALUES.has(normalizeBackgroundColor(value));
}

/**
 * 内側 chart/table からでも、最も近い visualization card root を capture 対象に昇格する。
 *
 * @remarks
 * export action は plot 内に置かれているが、保存対象は title / description / footnote を
 * 含むカード全体であるべきなので、共有 shell まで祖先解決してから capture する。
 */
function resolveVizCaptureElement(element: HTMLElement): HTMLElement {
  return element.closest<HTMLElement>(VIZ_CARD_ROOT_SELECTOR) ?? element;
}

/**
 * 現在見えている light/dark surface に沿う背景色を、対象要素の祖先から解決する。
 *
 * @remarks
 * AI 可視化カードの root 自体は transparent でも、実際には card / page surface の上に
 * 描かれている。PNG だけ透過にすると保存後の見え方が崩れるため、最も近い不透明背景を
 * 使って「画面で見えている面」をそのまま画像へ焼き込む。
 */
function resolveVizCaptureBackgroundColor(
  element: HTMLElement,
): string | undefined {
  if (typeof window === "undefined") return undefined;

  let current: HTMLElement | null = element;
  while (current) {
    const backgroundColor = window.getComputedStyle(current).backgroundColor;
    if (!isTransparentBackgroundColor(backgroundColor)) {
      return backgroundColor;
    }
    current = current.parentElement;
  }

  return undefined;
}

function resolveVizCaptureTarget(args: {
  element: HTMLElement | null;
}): { element: HTMLElement; backgroundColor: string | undefined } | null {
  if (!args.element) return null;

  const captureElement = resolveVizCaptureElement(args.element);
  return {
    element: captureElement,
    backgroundColor: resolveVizCaptureBackgroundColor(captureElement),
  };
}

export function downloadRowsAsCsv(args: {
  rows: VizRow[];
  columns: string[];
  baseName: string;
}): void {
  const { rows, columns, baseName } = args;
  if (columns.length === 0) return;

  const header = columns.map((column) => escapeCsvValue(column)).join(",");
  const body = rows.map((row) => {
    return columns.map((column) => escapeCsvValue(row[column])).join(",");
  });

  const csv = `\uFEFF${[header, ...body].join("\n")}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const filename = `${buildExportFileBaseName(baseName)}.csv`;
  downloadBlob(blob, filename);
}

export function downloadRowsAsJson(args: {
  rows: VizRow[];
  columns: string[];
  baseName: string;
}): void {
  const { rows, columns, baseName } = args;
  const exportRows =
    columns.length === 0
      ? rows
      : rows.map((row) => {
          const next: VizRow = {};
          for (const column of columns) {
            next[column] = row[column] ?? null;
          }
          return next;
        });

  const json = JSON.stringify(exportRows, null, 2);
  const blob = new Blob([json], { type: "application/json;charset=utf-8" });
  const filename = `${buildExportFileBaseName(baseName)}.json`;
  downloadBlob(blob, filename);
}

/**
 * 可視化カードを、現在見えている surface color 付きの PNG として保存する。
 *
 * @remarks
 * chart root が transparent でも、light / dark に応じた親 surface は画面上で見えている。
 * 保存画像だけ透過だと共有時に崩れるため、最も近い不透明背景色を capture option に渡す。
 */
export async function downloadVizAsPng(args: {
  element: HTMLElement | null;
  baseName: string;
}): Promise<void> {
  const target = resolveVizCaptureTarget({ element: args.element });
  if (!target) return;

  await downloadElementAsPng(target.element, {
    backgroundColor: target.backgroundColor,
    clone: cloneVizCardWithExpandedHorizontalScrollports,
    filename: `${buildExportFileBaseName(args.baseName)}.png`,
    dpr: 1,
  });
}

/**
 * 可視化カード全体を PNG としてクリップボードへコピーする。
 *
 * @remarks
 * download と copy で target / background 解決がずれると見た目が揺れるため、
 * 同じ capture target と背景色を使って export surface を統一する。
 */
export async function copyVizAsPng(args: {
  element: HTMLElement | null;
}): Promise<void> {
  const target = resolveVizCaptureTarget({ element: args.element });
  if (!target) return;

  await copyElementAsPng(target.element, {
    backgroundColor: target.backgroundColor,
    clone: cloneVizCardWithExpandedHorizontalScrollports,
    dpr: 1,
  });
}
